import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/PaymentProfilesService';
import { useState } from 'react';

export const usePaymentProfiles = (customerId: string) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    
    const sort = { column: 'created', direction: 'desc' as const };
    
    // INFINITE QUERY
    const paymentProfilesQuery = useInfiniteQuery({
        queryKey: ['payment-profiles', customerId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
        }
    });

    const fetchRecordsAction = () => {
        if (paymentProfilesQuery.hasNextPage) {
            paymentProfilesQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = paymentProfilesQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: paymentProfilesQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: paymentProfilesQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};